/**
 * 邮箱验证码验证组件（公开流程，不依赖登录态）。
 *
 * 用途：注册后「激活邮箱」与登录时「邮箱未验证」两种场景共用。
 * - 不发信责任在本组件之外：调用方负责把邮箱带进来；验证码由后端发到该邮箱。
 * - 注册场景后端已在 register 时下发过一次码，本组件默认不自动重发，用户可点「重新发送」。
 * - 验证成功后通过 onVerified 回调把后续动作交还父组件（跳转 / 重新登录等）。
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import { authApi } from '../api/auth.js';
import { isApiError } from '../api/errors.js';
import { toUserMessage } from '../api/errors.js';
import { Button } from './Button.js';
import { toast } from '../store/toast.js';

interface VerifyEmailProps {
  /** 待验证邮箱（来自注册表单或登录表单，已归一化小写） */
  email: string;
  /** 验证成功后的回调（跳转 / 自动重登等由父组件决定） */
  onVerified: () => void;
  /** 说明文案；缺省提示去收件箱查收 */
  hint?: string;
}

const RESEND_COUNTDOWN_SECONDS = 60;

export function VerifyEmail({ email, onVerified, hint }: VerifyEmailProps) {
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = window.setTimeout(() => setCountdown((value) => (value > 0 ? value - 1 : 0)), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  async function handleSend(): Promise<void> {
    setError(null);
    setSending(true);
    try {
      await authApi.sendVerifyCode(email);
      setSent(true);
      setCountdown(RESEND_COUNTDOWN_SECONDS);
      toast.success('验证码已发送', `请查收 ${email} 的邮件（若不在收件箱，请检查垃圾邮件）`);
    } catch (caught) {
      setError(toUserMessage(caught));
    } finally {
      setSending(false);
    }
  }

  async function handleVerify(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setVerifying(true);
    try {
      await authApi.verifyEmail(email, code);
      toast.success('邮箱已验证', '正在继续…');
      onVerified();
    } catch (caught) {
      // 验证码无效/过期：明确提示，保留输入让用户重试
      setError(
        isApiError(caught) && caught.code === 'UNAUTHORIZED'
          ? '验证码无效或已过期，请重新获取后再试'
          : toUserMessage(caught),
      );
    } finally {
      setVerifying(false);
    }
  }

  return (
    <form className="stack" onSubmit={handleVerify} noValidate>
      {error !== null ? (
        <div className="banner banner-danger" role="alert">
          <span className="banner-ico" aria-hidden="true">
            !
          </span>
          <span className="wrap-anywhere">{error}</span>
        </div>
      ) : null}

      <p className="field-hint">
        {hint ?? `我们已向 ${email} 发送验证码；若收件箱没有，请检查垃圾邮件文件夹。`}
      </p>

      <div className="field">
        <label className="field-label" htmlFor="verify-code">
          邮箱验证码
        </label>
        <input
          id="verify-code"
          className="input input-mono"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          placeholder="6 位数字"
          onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
        />
      </div>

      <div className="verify-actions">
        <Button type="button" variant="ghost" onClick={handleSend} disabled={sending || countdown > 0}>
          {countdown > 0 ? `重新发送(${countdown}s)` : sent ? '重新发送验证码' : '发送验证码'}
        </Button>
        <Button type="submit" variant="primary" loading={verifying} disabled={code.length !== 6}>
          验证并继续
        </Button>
      </div>
    </form>
  );
}
